/**
 * Converts raw API/network error messages into human-readable strings
 * suitable for display in the UI.
 *
 * Handles the common HTTP status codes and network failure messages that
 * surface from fetch() and the backend API layer.
 *
 * @param raw    - The raw error message string (e.g. err.message)
 * @param action - Short description of the operation that failed, used to
 *                 personalise the message (e.g. "load your profiles")
 */
export function friendlyError(raw: string, action: string): string {
  const lower = raw.toLowerCase()
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower === 'load failed') {
    return `Couldn't reach the server while trying to ${action}. Check your connection and try again in a few seconds.`
  }
  if (lower.includes('503') || lower.includes('service unavailable')) {
    return `The server is busy right now (another job may be running). Give it a minute and try again.`
  }
  if (lower.includes('504') || lower.includes('timeout') || lower.includes('timed out')) {
    return `The request took too long to complete. Try again, and if it keeps happening, the server might be under heavy load.`
  }
  if (lower.includes('401') || lower.includes('unauthorized')) {
    return `Your session expired. Refresh the page to log back in.`
  }
  if (lower.includes('403') || lower.includes('forbidden')) {
    return `You don't have permission for this action. Try logging out and back in.`
  }
  if (lower.includes('404') || lower.includes('not found')) {
    return `This ${action.includes('profile') ? 'profile' : 'item'} no longer exists. It may have been deleted.`
  }
  return raw
}

/**
 * The message of a caught value, or '' when it has none. Lets catch blocks
 * keep `catch (err)` typed as unknown and still fall back with `||`.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'object' && err !== null && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return ''
}
